import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TopBar, Footer } from "@/components/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Eye, GitBranch, FileSearch, ArrowRight, ShieldAlert,
  Sparkles, KeySquare, Fingerprint, History, Radar as RadarIcon,
  Route, BarChart3, Layers, Boxes, Coins, TrendingUp, FileText,
} from "lucide-react";

function TeaserCard() {
  const { t } = useTranslation();
  return (
    <div className="card-dark p-6 md:p-8">
      <div className="eyebrow text-slate-400">{t("landing.teaser.eyebrow")}</div>
      <div className="mt-5 grid grid-cols-2 gap-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("landing.teaser.maturity")}</div>
          <div className="mt-1 text-3xl font-bold accent-gradient">46 / 100</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("landing.teaser.priorities")}</div>
          <div className="mt-1 text-3xl font-bold text-white">{t("landing.teaser.priorities_val")}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("landing.teaser.deploy")}</div>
          <div className="mt-1 text-2xl font-bold text-white">{t("landing.teaser.deploy_val")}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("landing.teaser.budget")}</div>
          <div className="mt-1 text-2xl font-bold text-white">{t("landing.teaser.budget_val")}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{t("landing.teaser.budget_sub")}</div>
        </div>
      </div>
      <div className="mt-6 pt-5 border-t border-slate-800 space-y-3 text-sm">
        {[["bar_1", 38, "#FF9900"], ["bar_2", 24, "#DE0F72"], ["bar_3", 52, "#94a3b8"]].map(([k, pct, color]) => (
          <div key={k}>
            <div className="flex justify-between text-slate-300 text-xs mb-1.5">
              <span>{t(`landing.teaser.${k}`)}</span><span>{pct} / 100</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div style={{ width: `${pct}%`, background: color }} className="h-full rounded-full" />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-500 leading-relaxed">
        {t("landing.teaser.footnote")}<br />{t("landing.teaser.footnote_2")}
      </p>
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const signals = [
    { icon: KeySquare, key: "s1" }, { icon: Fingerprint, key: "s2" },
    { icon: History, key: "s3" }, { icon: ShieldAlert, key: "s4" },
  ];
  const pillars = [
    { icon: Eye, t: "landing.pillars.p1_title", b: "landing.pillars.p1_body" },
    { icon: GitBranch, t: "landing.pillars.p2_title", b: "landing.pillars.p2_body" },
    { icon: FileSearch, t: "landing.pillars.p3_title", b: "landing.pillars.p3_body" },
  ];
  const method = ["s1", "s2", "s3", "s4", "s5"];
  const outcomes = [
    { icon: RadarIcon, k: "o1" }, { icon: BarChart3, k: "o2" }, { icon: Route, k: "o3" },
    { icon: Boxes, k: "o4" }, { icon: Coins, k: "o5" }, { icon: TrendingUp, k: "o6" },
    { icon: Layers, k: "o7" }, { icon: FileText, k: "o8" },
  ];
  const faq = t("landing.faq.items", { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />

      <section className="halo-bg">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-600">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-fuchsia" />
              {t("landing.eyebrow")}
              <span className="ml-2 rounded-full bg-slate-950 text-white px-2 py-0.5 text-[10px]">{t("landing.eyebrow_free")}</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900"
              data-testid="landing-title"
            >
              {t("landing.h1_part1")} <span className="accent-gradient">{t("landing.h1_part2")}</span>{t("landing.h1_part3")}
            </motion.h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">{t("landing.sub")}</p>
            <div className="mt-9 flex flex-wrap gap-3 items-center">
              <Link to="/simuler" className="btn-primary" data-testid="hero-cta-primary">
                {t("landing.cta_primary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/exemple" className="text-sm font-semibold text-slate-700 hover:text-slate-900 underline decoration-slate-300 underline-offset-4" data-testid="hero-cta-secondary">
                {t("landing.cta_secondary")}
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-fuchsia"/> {t("landing.trust_1")}</span>
              <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-orange"/> {t("landing.trust_2")}</span>
              <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-slate-400"/> {t("landing.trust_3")}</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative z-10"><TeaserCard /></div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">{t("landing.signals.eyebrow")}</div>
            <h2 className="section-h mt-3">{t("landing.signals.h2")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t("landing.signals.sub")}</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {signals.map(({ icon: Icon, key }) => (
              <div key={key} className="card-flat p-5 flex items-start gap-3">
                <Icon className="h-4 w-4 text-brand-fuchsia mt-0.5" />
                <p className="text-sm text-slate-800 leading-relaxed">{t(`landing.signals.${key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="max-w-3xl">
          <div className="eyebrow">{t("landing.pillars.eyebrow")}</div>
          <h2 className="mt-3 section-h">{t("landing.pillars.h2")}</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">{t("landing.pillars.sub")}</p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {pillars.map(({ icon: Icon, t: tk, b }) => (
            <div key={tk} className="card-flat p-7 hover:border-slate-300 transition-colors" data-testid={`benefit-${tk}`}>
              <div className="h-11 w-11 rounded-lg bg-slate-950 flex items-center justify-center">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="mt-5 text-xl font-bold text-slate-900">{t(tk)}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{t(b)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{t("landing.method.eyebrow")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t("landing.method.h2_l1")}<br />{t("landing.method.h2_l2")}</h2>
            <p className="mt-5 text-slate-400 leading-relaxed">{t("landing.method.sub")}</p>
            <Link to="/simuler" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-6 py-3 font-semibold hover:bg-slate-100 transition-colors" data-testid="method-cta">
              {t("landing.method.cta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
            {method.map((s, i) => (
              <div key={s} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-white/10 border border-slate-700 text-white text-sm font-semibold flex items-center justify-center">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-white font-semibold text-lg">{t(`landing.method.${s}_label`)}</div>
                </div>
                <p className="mt-3 text-slate-400 text-sm leading-relaxed">{t(`landing.method.${s}_detail`)}</p>
              </div>
            ))}
            <div className="rounded-xl border border-brand-fuchsia/30 bg-fuchsia-500/5 p-5">
              <div className="text-brand-fuchsia text-xs font-semibold uppercase tracking-widest">{t("landing.method.livrable_label")}</div>
              <p className="mt-2 text-white text-sm leading-relaxed">{t("landing.method.livrable_detail")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow">{t("landing.outcomes.eyebrow")}</div>
            <h2 className="section-h mt-3">{t("landing.outcomes.h2")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t("landing.outcomes.sub")}</p>
            <Link to="/exemple" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4">
              {t("landing.outcomes.link")} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {outcomes.map(({ icon: Icon, k }) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <Icon className="h-4 w-4 text-brand-fuchsia" />
                <span className="text-sm text-slate-800 font-medium">{t(`landing.outcomes.${k}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="eyebrow">{t("landing.faq.eyebrow")}</div>
          <h2 className="section-h mt-3">{t("landing.faq.h2")}</h2>
          <div className="mt-8 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            <Accordion type="single" collapsible className="w-full">
              {Array.isArray(faq) && faq.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-0 px-6">
                  <AccordionTrigger className="text-left text-base font-semibold text-slate-900 hover:no-underline hover:text-slate-700">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="rounded-2xl bg-slate-950 text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full opacity-30 pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(255,153,0,0.5), transparent 60%)" }} />
          <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full opacity-30 pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(222,15,114,0.5), transparent 60%)" }} />
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                <Sparkles className="h-3 w-3 text-brand-orange" /> {t("landing.cta_final.eyebrow")}
              </div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                {t("landing.cta_final.h2_l1")}<br />{t("landing.cta_final.h2_l2")}
              </h2>
              <p className="mt-3 text-slate-300 max-w-2xl">{t("landing.cta_final.sub")}</p>
            </div>
            <Link to="/simuler" className="rounded-full bg-white text-slate-950 px-6 py-3 font-semibold hover:bg-slate-100 transition-colors inline-flex items-center gap-2" data-testid="footer-cta-start">
              {t("landing.cta_final.btn")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
